import type { PblLawyerProfile, PblVettingChecklist, PblVettingReviewPayload, PblVettingRound } from '@/types/pbl';
import { formatPblDisplayDate } from '@/utils/pblDate';

export type PblVettingReviewAction = 'approve' | 'resubmit';

export type PblVettingResubmissionDetails = {
  returnReasonCode?: string;
  returnReason?: string;
  returnReasonOther?: string;
  reviewerComments?: string;
};

export type PblVettingResubmitReason = {
  reason: string;
  date: string;
};

export type PblVettingDocument = {
  name: string;
  url?: string;
};

export type PblVettingChecklistGroup = {
  key: keyof PblVettingChecklist;
  title: string;
  items: {
    key: string;
    label: string;
  }[];
};

type PblVettingChecklistGroupDefinition = {
  key: keyof PblVettingChecklist;
  titleKey: string;
  items: {
    key: string;
    labelKey: string;
  }[];
};

export const pblVettingChecklistErrorMessage =
  'Please complete all vetting checklist items before approving this application.';

const pblVettingChecklistGroupDefinitions: PblVettingChecklistGroupDefinition[] = [
  {
    key: 'basic_eligibility',
    titleKey: 'pbl.pagesHardcoded.vettingDetail.vettingDetailState.basicEligibilityCheck',
    items: [
      {
        key: 'valid_hk_practising_certificate',
        labelKey: 'pbl.pagesHardcoded.vettingDetail.vettingDetailState.applicantIsAPractisingSolicitorInHongKong',
      },
      {
        key: 'minimum_three_years_post_admission',
        labelKey: 'pbl.pagesHardcoded.vettingDetail.vettingDetailState.applicantHasAMinimumOfThreeYearsPost',
      },
      {
        key: 'relevant_scheme_service_experience',
        labelKey:
          'pbl.pagesHardcoded.vettingDetail.vettingDetailState.applicantHasDemonstratedRelevantExperienceForTheCategories',
      },
      {
        key: 'no_conviction_or_disciplinary_declaration',
        labelKey:
          'pbl.pagesHardcoded.vettingDetail.vettingDetailState.noDeclarationByApplicantOfConvictionsOtherThan',
      },
      {
        key: 'no_bankruptcy_or_financial_misconduct',
        labelKey:
          'pbl.pagesHardcoded.vettingDetail.vettingDetailState.noDeclarationByApplicantOfUndischargedBankruptcyOr',
      },
    ],
  },
  {
    key: 'documentation_completeness',
    titleKey: 'pbl.pagesHardcoded.vettingDetail.vettingDetailState.documentationCompletenessCheck',
    items: [
      {
        key: 'registration_form_completed_signed_dated',
        labelKey: 'pbl.pagesHardcoded.vettingDetail.vettingDetailState.lawyerRegistrationFormFullyCompletedSignedAndDated',
      },
      {
        key: 'standardized_cv_bilingual_submitted',
        labelKey:
          'pbl.pagesHardcoded.vettingDetail.vettingDetailState.standardisedCvBothChineseAndEnglishVersionSubmitted',
      },
      {
        key: 'firm_authorisation_letter_valid',
        labelKey:
          'pbl.pagesHardcoded.vettingDetail.vettingDetailState.firmAuthorisationLetterSignedByFirmPartnerUploaded',
      },
    ],
  },
  {
    key: 'conduct_check_review',
    titleKey: 'pbl.pagesHardcoded.vettingDetail.vettingDetailState.conductCheckReview',
    items: [
      {
        key: 'yes_answers_have_sufficient_explanation',
        labelKey:
          'pbl.pagesHardcoded.vettingDetail.vettingDetailState.allYesAnswersIfAnyAreAccompaniedBy',
      },
      {
        key: 'no_eligibility_affecting_issues_identified',
        labelKey:
          'pbl.pagesHardcoded.vettingDetail.vettingDetailState.noIssuesIdentifiedThatAffectEligibilityUnderThe',
      },
    ],
  },
];

export const getPblVettingChecklistGroups = (t: (key: string) => string): PblVettingChecklistGroup[] =>
  pblVettingChecklistGroupDefinitions.map((group) => ({
    key: group.key,
    title: t(group.titleKey),
    items: group.items.map((item) => ({
      key: item.key,
      label: t(item.labelKey),
    })),
  }));

const createPblVettingChecklistWithValue = (value: boolean): PblVettingChecklist => ({
  basic_eligibility: {
    valid_hk_practising_certificate: value,
    minimum_three_years_post_admission: value,
    relevant_scheme_service_experience: value,
    no_conviction_or_disciplinary_declaration: value,
    no_bankruptcy_or_financial_misconduct: value,
  },
  documentation_completeness: {
    registration_form_completed_signed_dated: value,
    standardized_cv_bilingual_submitted: value,
    firm_authorisation_letter_valid: value,
  },
  conduct_check_review: {
    yes_answers_have_sufficient_explanation: value,
    no_eligibility_affecting_issues_identified: value,
  },
});

export const createEmptyPblVettingChecklist = () => createPblVettingChecklistWithValue(false);

export const createCompletedPblVettingChecklist = () => createPblVettingChecklistWithValue(true);

export const isPblVettingChecklistComplete = (checklist?: PblVettingChecklist | null) =>
  pblVettingChecklistGroupDefinitions.every((group) =>
    group.items.every((item) => Boolean((checklist?.[group.key] as Record<string, boolean> | undefined)?.[item.key]))
  );

export const buildPblVettingReviewPayload = (
  action: PblVettingReviewAction,
  resubmissionDetails: PblVettingResubmissionDetails = {},
  checklist?: PblVettingChecklist
): PblVettingReviewPayload => {
  if (action === 'approve') {
    return checklist ? { status: 'approved', vetting_checklist: checklist } : { status: 'approved' };
  }

  return {
    status: 'resubmit_required',
    return_reason_code: resubmissionDetails.returnReasonCode?.trim(),
    return_reason: resubmissionDetails.returnReason?.trim(),
    return_reason_other: resubmissionDetails.returnReasonOther?.trim(),
    reviewer_comments: resubmissionDetails.reviewerComments?.trim(),
  };
};

const textOrUndefined = (value: unknown) => {
  if (typeof value !== 'string') return undefined;

  const trimmed = value.trim();
  return trimmed || undefined;
};

const normalizeBackendFileUrl = (url?: string) => {
  if (!url) return undefined;

  const storageDomains = [
    'https://dealmakingstoragerpro.blob.core.windows.net/',
    'https://dealmakingstoragedev.blob.core.windows.net/',
  ];
  const storageDomain = storageDomains.find((domain) => url.startsWith(domain));

  if (storageDomain) return `/api/${url.slice(storageDomain.length)}`;
  return url;
};

const toRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;

const extractBackendDocument = (candidate?: unknown): PblVettingDocument | null => {
  if (!candidate) return null;

  if (Array.isArray(candidate)) {
    for (const item of candidate) {
      const document = extractBackendDocument(item);
      if (document) return document;
    }

    return null;
  }

  const record = toRecord(candidate);
  if (!record) return null;

  const nestedDocument = extractBackendDocument(record.file) || extractBackendDocument(record.document);
  const name =
    textOrUndefined(record.name) ||
    textOrUndefined(record.file_name) ||
    textOrUndefined(record.original_name) ||
    textOrUndefined(record.filename) ||
    textOrUndefined(record.title);
  const url = normalizeBackendFileUrl(
    textOrUndefined(record.url) ||
      textOrUndefined(record.href) ||
      textOrUndefined(record.path) ||
      textOrUndefined(record.download_url)
  );

  if (name) return { name, url };
  return nestedDocument;
};

const extractBackendDocuments = (candidate?: unknown): PblVettingDocument[] => {
  if (!candidate) return [];

  if (Array.isArray(candidate)) {
    return candidate
      .map((item) => extractBackendDocument(item))
      .filter((document): document is PblVettingDocument => Boolean(document));
  }

  const document = extractBackendDocument(candidate);
  return document ? [document] : [];
};

const uniqueDocuments = (documents: PblVettingDocument[]) => {
  const seen = new Set<string>();

  return documents.filter((document) => {
    const key = `${document.name}|${document.url || ''}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const getRoundReason = (round: PblVettingRound) =>
  textOrUndefined(round.return_reason_other) || textOrUndefined(round.return_reason);

export const getPblVettingRoundDate = (round: PblVettingRound) =>
  formatPblDisplayDate(
    round.reviewed_at_display ||
      round.reviewed_at ||
      round.updated_at_display ||
      round.updated_at ||
      round.created_at_display ||
      round.created_at
  );

export const getPblVettingChecklistRounds = (application: PblLawyerProfile) =>
  [...(application.vetting_rounds || [])]
    .filter((round) => Boolean(round.vetting_checklist))
    .sort((left, right) => (right.round_no || 0) - (left.round_no || 0));

export const getPblVettingResubmitReasons = (application: PblLawyerProfile): PblVettingResubmitReason[] => {
  const rounds = [...(application.vetting_rounds || [])]
    .filter((round) => round.status === 'resubmit_required' || getRoundReason(round))
    .sort((left, right) => (right.round_no || 0) - (left.round_no || 0));

  const roundReasons = rounds
    .map((round) => {
      const reason = getRoundReason(round);
      if (!reason) return null;

      return {
        reason,
        date: getPblVettingRoundDate(round),
      };
    })
    .filter((item): item is PblVettingResubmitReason => Boolean(item));

  if (roundReasons.length > 0) return roundReasons;

  const legacyReason = textOrUndefined(application.return_reason_other) || textOrUndefined(application.return_reason);
  if (!legacyReason) return [];

  return [
    {
      reason: legacyReason,
      date: formatPblDisplayDate(
        application.reviewed_at_display ||
          application.reviewed_at ||
          application.updated_at ||
          application.submitted_at ||
          application.created_at
      ),
    },
  ];
};

export const getPblVettingFirmAuthorisationDocument = (application: PblLawyerProfile): PblVettingDocument | null => {
  const directDocument =
    extractBackendDocument(application.firm_authorisation_file) ||
    extractBackendDocument({
      name: application.firm_authorisation_file_name,
      url: application.firm_authorisation_file_url,
      path: application.firm_authorisation_file_path,
    });

  if (directDocument?.url) return directDocument;

  const roundDocument = [...(application.vetting_rounds || [])]
    .sort((left, right) => (right.round_no || 0) - (left.round_no || 0))
    .map((round) => extractBackendDocument(round.submitted_payload?.firm_authorisation_file))
    .find((document): document is PblVettingDocument => Boolean(document?.url));

  return roundDocument || directDocument;
};

export const getPblVettingConductSupportingDocuments = (application: PblLawyerProfile): PblVettingDocument[] => {
  const directDocuments = [
    ...extractBackendDocuments(application.conduct_supporting_document),
    ...extractBackendDocuments(application.conduct_supporting_document_file),
  ];
  const roundDocuments = [...(application.vetting_rounds || [])]
    .sort((left, right) => (right.round_no || 0) - (left.round_no || 0))
    .flatMap((round) => [
      ...extractBackendDocuments(round.supporting_files),
      ...extractBackendDocuments(round.submitted_payload?.conduct_supporting_document),
      ...extractBackendDocuments(round.submitted_payload?.conduct_supporting_document_file),
    ]);
  const legacyDocuments = (application.conduct_supporting_document_names || []).map((name) => ({ name }));

  return uniqueDocuments([...directDocuments, ...roundDocuments, ...legacyDocuments]);
};

export const getPblVettingCompletedProBonoCaseCount = (application: PblLawyerProfile) =>
  application.completed_case_count ?? '-';

export const shouldShowPblVettingReviewActions = (application: PblLawyerProfile) =>
  (application.vetting_status || application.status || 'pending_review') === 'pending_review';
